import dayjs from 'dayjs';
import { DashboardDateRange } from '@restavo/shared/enums/dashboard';

/**
 * Windows up to this many days are drawn as daily bars; longer ones collapse to months.
 * 62 rather than a round 60 so that a two-calendar-month custom range still reads as days
 * instead of two lonely bars.
 */
export const BUCKET_DAY_LIMIT = 62;

export type DashboardBucket = 'day' | 'month';

/**
 * The start/end of a given calendar day (`YYYY-MM-DD`) as anchored to `timezone` — the
 * shared building block behind both a preset's day-granularity window (today, yesterday)
 * and a custom range's picked days. Parsing a bare date STRING with `dayjs.tz` (rather than
 * handing it a `Date` instant) is what makes the anchoring literal: the string's Y-M-D
 * becomes that exact calendar day in `timezone`, regardless of what zone produced the
 * string.
 */
function startOfDayInTz(day: string, timezone: string): Date {
  return dayjs.tz(day, timezone).startOf('day').toDate();
}

function endOfDayInTz(day: string, timezone: string): Date {
  return dayjs.tz(day, timezone).endOf('day').toDate();
}

/**
 * Granularity is a property of the window's LENGTH, not of which preset produced it. Keeping
 * that rule in one function is the whole point of splitting `bucket` out of `dateRange`:
 * the server used to infer granularity from a preset name and got the window wrong.
 */
export function bucketForWindow(startDate: Date | null, endDate: Date): DashboardBucket {
  if (!startDate) {
    return 'month';
  }
  const days = dayjs(endDate).diff(dayjs(startDate), 'day');
  return days <= BUCKET_DAY_LIMIT ? 'day' : 'month';
}

/**
 * A preset's window, computed in the RESTAURANT's timezone. "Today" must mean the
 * restaurant's today: an operator in Ho Chi Minh opening the dashboard from a laptop still
 * set to UTC was otherwise shown yesterday's takings for seven hours every morning.
 *
 * `startDate: null` means unbounded and is sent as an omitted `startDate`.
 */
export function windowForPreset(
  preset: DashboardDateRange,
  timezone: string
): { startDate: Date | null; endDate: Date } {
  const now = dayjs().tz(timezone);

  switch (preset) {
    case DashboardDateRange.YESTERDAY: {
      const day = now.subtract(1, 'day').format('YYYY-MM-DD');
      return { startDate: startOfDayInTz(day, timezone), endDate: endOfDayInTz(day, timezone) };
    }
    case DashboardDateRange.WEEKLY:
      // isoWeek, not week: dayjs defaults to a Sunday start and the customers' week begins
      // on Monday.
      return { startDate: now.startOf('isoWeek').toDate(), endDate: now.endOf('isoWeek').toDate() };
    case DashboardDateRange.MONTHLY:
      return { startDate: now.startOf('month').toDate(), endDate: now.endOf('month').toDate() };
    case DashboardDateRange.YEARLY:
      return { startDate: now.startOf('year').toDate(), endDate: now.endOf('year').toDate() };
    case DashboardDateRange.ALL:
      // Genuinely unbounded now. The old three-years-ago floor silently hid older trading.
      return { startDate: null, endDate: now.endOf('day').toDate() };
    case DashboardDateRange.DAILY:
    // CUSTOM deliberately shares the DAILY arm rather than getting its own case: callers that
    // pick a custom range supply their own start/end bounds directly, and `DateRangeMenu`
    // returns early on CUSTOM (it stages the picker instead of calling `onChange`) — so this
    // function is never actually asked to compute a window FOR a custom range. Don't "fix"
    // this arm into something CUSTOM-specific; there is nothing here for it to reach.
    case DashboardDateRange.CUSTOM:
    default: {
      const day = now.format('YYYY-MM-DD');
      return { startDate: startOfDayInTz(day, timezone), endDate: endOfDayInTz(day, timezone) };
    }
  }
}

/**
 * Turns the Dates a range `DatePicker` hands back into the restaurant's calendar-day
 * boundaries. Flatpickr's `onChange` gives local-midnight `Date`s for whichever days were
 * clicked — local to the BROWSER, not the restaurant — so reading each one's Y-M-D and
 * re-anchoring it with `dayjs.tz(dateString, timezone)` gives "1 Sep" through "15 Sep" in the
 * restaurant's timezone regardless of what zone the browser happens to be in.
 *
 * Without this, `dates[1]` used directly is local midnight of the LAST day, which excludes
 * that entire day from every range query built from it — the custom-range counterpart of the
 * "today must mean the restaurant's today" fix in `windowForPreset` above.
 */
export function windowForCustomRange(
  pickedStart: Date,
  pickedEnd: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  const startDay = dayjs(pickedStart).format('YYYY-MM-DD');
  const endDay = dayjs(pickedEnd).format('YYYY-MM-DD');
  return {
    startDate: startOfDayInTz(startDay, timezone),
    endDate: endOfDayInTz(endDay, timezone),
  };
}
